import nBossConnection from 'modules/base/service/nBossConnection';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { FaUserShield } from 'react-icons/fa';
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { logoutUser, getUserData } from 'app/auth/store/userSlice';
import { loginNextStep } from 'app/auth/store/loginSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import AccessManagementService from 'app/admin/user/service/AccessManagementService';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import { useTranslation } from 'react-i18next';

const STEP = { LOGGED_IN: 'loggedIn', CHOOSE_GROUP: 'chooseGroup', CHOOSE_ROLE: 'chooseRole', NO_GROUP: 'noGroup', NO_ROLE: 'noRole' };
const isStepSuccess = (r) => { if (!r) return false; if (typeof r.lastStepSuccessful === 'boolean') return r.lastStepSuccessful; if (typeof r.successful === 'boolean') return r.successful; return true; };
const getNextStep = (r) => (r && r.nextStep != null ? String(r.nextStep) : null);
const fmtRole = (r) => { const s = String(r || ''); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; };

function UserMenu(props) {
	const dispatch = useDispatch(); const history = useHistory(); const { t } = useTranslation('auth');
	const user = useSelector(({ auth }) => auth.user); const login = useSelector(({ auth }) => auth.login);

	const [userMenu, setUserMenu] = useState(null); const [switching, setSwitching] = useState(false);
	const userMenuClick = (event) => setUserMenu(event.currentTarget); const userMenuClose = () => setUserMenu(null);

	const name = user?.data?.name || '';
	const groups = user?.data?.userGroups ?? login?.client?.userGroups ?? login?.meta?.userGroups ?? [];
	const activeGroupId = user?.data?.activeUserGroupId ?? user?.data?.userGroupId ?? user?.activeUserGroupId ?? user?.userGroupId ?? login?.meta?.userGroupId ?? login?.meta?.groupId ?? login?.client?.userGroupId ?? login?.client?.activeUserGroupId ?? null;
	const hasGroups = Array.isArray(groups) && groups.length > 0;

	const roles = Array.isArray(user?.data?.roles) ? user.data.roles : (Array.isArray(login?.client?.roles) ? login.client.roles : (Array.isArray(user?.roles) ? user.roles : []));
	const currentRole = user?.data?.role ?? user?.role ?? login?.meta?.role ?? '';
	const hasRoles = Array.isArray(roles) && roles.length > 0;
	const rolesLine = hasRoles ? roles.filter(Boolean).map(fmtRole).join(', ') : '';

	const handleSwitchGroup = useCallback(async (groupId) => {
		if (switching) return; setSwitching(true);
		try {
			const result = await AccessManagementService.changeUserGroup(groupId); const step = getNextStep(result);
			if (result?.isLoggedIn === true || step === STEP.LOGGED_IN) { dispatch(getUserData()); window.location.reload(); return; }
			if (step) { dispatch(loginNextStep({ nextStep: step, meta: result?.meta ?? null, client: result?.client ?? null })); userMenuClose(); history.push('/login'); return; }
			if (!isStepSuccess(result)) { dispatch(showMessage({ message: 'Group switch failed. Please try again.' })); return; }
			dispatch(getUserData()); window.location.reload();
		} catch { dispatch(showMessage({ message: 'Network error. Please try again.' })); }
		finally { setSwitching(false); }
	}, [dispatch, history, switching]);

	const handleSwitchRole = useCallback(async (role) => {
		if (switching) return; setSwitching(true);
		try {
			const result = await UserLoginService.changeRole(role); const step = getNextStep(result);
			if (result?.isLoggedIn === true || step === STEP.LOGGED_IN) { dispatch(getUserData()); window.location.reload(); return; }
			if (step) { dispatch(loginNextStep({ nextStep: step, meta: result?.meta ?? null, client: result?.client ?? null })); userMenuClose(); history.push('/login'); return; }
			if (!isStepSuccess(result)) { dispatch(showMessage({ message: 'Role switch failed. Please try again.' })); return; }
			dispatch(getUserData()); window.location.reload();
		} catch { dispatch(showMessage({ message: 'Network error. Please try again.' })); }
		finally { setSwitching(false); }
	}, [dispatch, history, switching]);

	return (
		<>
			<Button className="min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6" onClick={userMenuClick}>
				<div className="hidden md:flex flex-col mx-4 items-end">
					<Typography component="span" className="font-semibold flex">{name}</Typography>
					<Typography className="text-11 font-medium capitalize" color="textSecondary">{rolesLine}</Typography>
				</div>
				{user?.data?.profilePicture ? (<Avatar className="md:mx-4" alt={`${name} photo`} src={`${nBossConnection.baseUrl}fileEntityControl/getFile?key=${user.data.profilePicture.key}`} />) : (<Avatar className="md:mx-4">{name}</Avatar>)}
			</Button>

			<Popover open={Boolean(userMenu)} anchorEl={userMenu} onClose={userMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }} classes={{ paper: 'py-8' }}>
				{!hasRoles ? (
					<>
						<MenuItem component={Link} to="/login" role="button"><ListItemIcon className="min-w-40"><Icon>lock</Icon></ListItemIcon><ListItemText primary={t('LOGIN')} /></MenuItem>
						<MenuItem component={Link} to="/register" role="button"><ListItemIcon className="min-w-40"><Icon>person_add</Icon></ListItemIcon><ListItemText primary={t('REGISTER')} /></MenuItem>
					</>
				) : (
					<>
						<MenuItem component={Link} to={`/profile/${user?.data?.id}`} onClick={userMenuClose} role="button"><ListItemIcon className="min-w-40"><Icon>account_circle</Icon></ListItemIcon><ListItemText primary={t('MY_PROFILE')} /></MenuItem>
						<Divider className="my-4" />

						{hasRoles ? (
							<>
								<Typography className="px-16 py-6 text-12 opacity-60 uppercase">Switch role</Typography>
								{roles.map((r) => {
									const isActive = currentRole && String(currentRole) === String(r); return (
										<MenuItem key={String(r)} disabled={switching || isActive} onClick={() => handleSwitchRole(r)}>
											<ListItemIcon className="min-w-32">{isActive ? (<Icon>check_circle</Icon>) : (<FaUserShield size={18} />)}</ListItemIcon>
											<ListItemText primary={fmtRole(r)} />
										</MenuItem>
									);
								})}
								<Divider className="my-4" />
							</>
						) : null}

						{hasGroups ? (
							<>
								<Typography className="px-16 py-6 text-12 opacity-60 uppercase">Switch group</Typography>
								{groups.map((g) => {
									const isActive = activeGroupId != null && String(activeGroupId) === String(g.id); return (
										<MenuItem key={g.id} disabled={switching || isActive} onClick={() => handleSwitchGroup(g.id)}>
											<ListItemIcon className="min-w-32"><Icon>{isActive ? 'check_circle' : 'group'}</Icon></ListItemIcon>
											<ListItemText primary={g.name} />
										</MenuItem>
									);
								})}
								<Divider className="my-4" />
							</>
						) : null}

						<MenuItem onClick={() => { dispatch(logoutUser()); userMenuClose(); }}><ListItemIcon className="min-w-40"><Icon>exit_to_app</Icon></ListItemIcon><ListItemText primary={t('LOGOUT')} /></MenuItem>
					</>
				)}
			</Popover>
		</>
	);
}

export default UserMenu;
